import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, FormControl, InputLabel, Select, MenuItem,
  Tabs, Tab, Paper, Table, TableHead, TableBody,
  TableRow, TableCell, Chip, CircularProgress, useMediaQuery,
} from '@mui/material';
import { smartWarehouseService } from '../services/smartWarehouseService';
import dashboardService, {
  OverviewReport, PickerPerformance, PackingOrderReport,
  ReceivingReportItem, CountingReportItem, ShortPickReportItem,
} from '../services/dashboardService';
import { useTranslation } from 'react-i18next';
import { AppDatePicker } from '../../common/AppDatePicker';
import ReportsHubBackButton from '../../reports/hub/ReportsHubBackButton';

interface Warehouse { id: string; warehouseName: string; }

const WmsReports: React.FC = () => {
  const { t } = useTranslation('warehouse');
  const isMobile = useMediaQuery('(max-width:900px)');
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [tab, setTab] = useState(0);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);

  const [overview, setOverview] = useState<OverviewReport | null>(null);
  const [pickers, setPickers] = useState<PickerPerformance[]>([]);
  const [packers, setPackers] = useState<PackingOrderReport[]>([]);
  const [receiving, setReceiving] = useState<ReceivingReportItem[]>([]);
  const [counting, setCounting] = useState<CountingReportItem[]>([]);
  const [shortPicks, setShortPicks] = useState<ShortPickReportItem[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const list = await smartWarehouseService.getAllList();
        setWarehouses(list);
        if (list.length > 0) setSelectedWarehouse(list[0].id);
      } catch { /* ignore */ }
    })();
  }, []);

  const range = { dateFrom, dateTo };

  const fetchData = useCallback(async () => {
    if (!selectedWarehouse) return;
    setLoading(true);
    try {
      switch (tab) {
        case 0: {
          const data = await dashboardService.getOverviewReport(selectedWarehouse, range);
          setOverview(data);
          break;
        }
        case 1: {
          const [p, sp] = await Promise.all([
            dashboardService.getPickingReport(selectedWarehouse, range),
            dashboardService.getShortPicksReport(selectedWarehouse, range),
          ]);
          setPickers(p);
          setShortPicks(sp);
          break;
        }
        case 2: {
          const data = await dashboardService.getPackingReport(selectedWarehouse, range);
          setPackers(data);
          break;
        }
        case 3: {
          const data = await dashboardService.getReceivingReport(selectedWarehouse, range);
          setReceiving(data);
          break;
        }
        case 4: {
          const data = await dashboardService.getCountingReport(selectedWarehouse, range);
          setCounting(data);
          break;
        }
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [selectedWarehouse, tab, dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <ReportsHubBackButton reportPath="/wms/raporlar" />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{t('pages.reports.title')}</Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{t('common.warehouse')}</InputLabel>
          <Select value={selectedWarehouse} onChange={e => setSelectedWarehouse(e.target.value)} label={t('common.warehouse')}>
            {warehouses.map(w => <MenuItem key={w.id} value={w.id}>{w.warehouseName}</MenuItem>)}
          </Select>
        </FormControl>
        <AppDatePicker label={t('pages.reports.startDate')} size="small" value={dateFrom} onChange={setDateFrom} />
        <AppDatePicker label={t('pages.reports.endDate')} size="small" value={dateTo} onChange={setDateTo} />
      </Box>

      <Paper sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant={isMobile ? 'scrollable' : 'standard'} allowScrollButtonsMobile>
          <Tab label={t('pages.reports.tabs.overview')} />
          <Tab label={t('pages.reports.tabs.picking')} />
          <Tab label={t('pages.reports.tabs.packing')} />
          <Tab label={t('pages.reports.tabs.receiving')} />
          <Tab label={t('pages.reports.tabs.counting')} />
        </Tabs>
      </Paper>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <>
          {tab === 0 && <OverviewTab data={overview} />}
          {tab === 1 && <PickingTab pickers={pickers} shortPicks={shortPicks} isMobile={isMobile} />}
          {tab === 2 && <PackingTab orders={packers} isMobile={isMobile} />}
          {tab === 3 && <ReceivingTab items={receiving} isMobile={isMobile} />}
          {tab === 4 && <CountingTab items={counting} isMobile={isMobile} />}
        </>
      )}
    </Box>
  );
};

const OverviewTab: React.FC<{ data: OverviewReport | null }> = ({ data }) => {
  const { t } = useTranslation('warehouse');
  if (!data) return <Typography color="text.secondary">{t('pages.reports.loadingData')}</Typography>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{t('pages.reports.overview.orderFulfillment')}</Typography>
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <StatItem label={t('pages.reports.overview.totalOrders')} value={data.totalOrders} />
          <StatItem label={t('pages.reports.overview.fulfilled')} value={`${data.fulfilledOrders} (%${data.fulfillmentRate})`} />
          <StatItem label={t('pages.reports.overview.cancelled')} value={`${data.cancelledOrders} (%${data.cancellationRate})`} />
          <StatItem label={t('pages.reports.overview.pending')} value={data.pendingOrders} />
          <StatItem label={t('pages.reports.overview.avgFulfillment')} value={t('pages.reports.overview.avgFulfillmentValue', { value: data.avgFulfillmentHours })} />
        </Box>
      </Paper>

      {data.dailyData.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{t('pages.reports.overview.dailyChart')}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 120, overflow: 'auto' }}>
            {data.dailyData.map((d, idx) => {
              const maxVal = Math.max(...data.dailyData.map(dd => Math.max(dd.received, dd.fulfilled, dd.cancelled)), 1);
              return (
                <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', minWidth: 50 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: 80 }}>
                    <Box sx={{ width: 12, bgcolor: '#2196f3', height: `${(d.received / maxVal) * 100}%`, borderRadius: '2px 2px 0 0', minHeight: d.received > 0 ? 2 : 0 }} />
                    <Box sx={{ width: 12, bgcolor: '#4caf50', height: `${(d.fulfilled / maxVal) * 100}%`, borderRadius: '2px 2px 0 0', minHeight: d.fulfilled > 0 ? 2 : 0 }} />
                    <Box sx={{ width: 12, bgcolor: '#ef5350', height: `${(d.cancelled / maxVal) * 100}%`, borderRadius: '2px 2px 0 0', minHeight: d.cancelled > 0 ? 2 : 0 }} />
                  </Box>
                  <Typography variant="caption" sx={{ fontSize: 10 }}>{d.date}</Typography>
                </Box>
              );
            })}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <LegendDot color="#2196f3" label={t('pages.reports.overview.legendReceived')} />
            <LegendDot color="#4caf50" label={t('pages.reports.overview.legendFulfilled')} />
            <LegendDot color="#ef5350" label={t('pages.reports.overview.legendCancelled')} />
          </Box>
        </Paper>
      )}
    </Box>
  );
};

const PickingTab: React.FC<{ pickers: PickerPerformance[]; shortPicks: ShortPickReportItem[]; isMobile?: boolean }> = ({
  pickers, shortPicks,
  isMobile,
}) => {
  const { t } = useTranslation('warehouse');
  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t('pages.reports.picking.performanceTitle')}</Typography>
        {pickers.length === 0 ? (
          <Paper sx={{ p: 1.5 }}><Typography color="text.secondary">{t('pages.reports.noData')}</Typography></Paper>
        ) : pickers.map((p, i) => (
          <Paper key={i} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{p.pickerName}</Typography>
            <Typography variant="body2">{p.waveCount} iş emri • {p.itemCount} ürün</Typography>
            <Typography variant="caption" color="text.secondary">{t('pages.reports.minuteValue', { value: p.avgDuration })}</Typography>
            <Box sx={{ mt: 0.75, display: 'flex', gap: 1 }}>
              <Chip label={`%${p.accuracy}`} size="small" color={p.accuracy >= 99 ? 'success' : p.accuracy >= 95 ? 'warning' : 'error'} />
              <Chip label={t('pages.reports.picking.efficiencyValue', { value: p.efficiency })} size="small" variant="outlined" />
            </Box>
          </Paper>
        ))}
        {shortPicks.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'error.main' }}>{t('pages.reports.picking.shortPickTitle')}</Typography>
            {shortPicks.map((sp, i) => (
              <Paper key={i} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{sp.productName}</Typography>
                <Typography variant="caption" display="block" color="text.secondary">{sp.date}</Typography>
                <Typography variant="body2">{sp.pickerName}</Typography>
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{sp.locationCode}</Typography>
              </Paper>
            ))}
          </>
        )}
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <Paper sx={{ overflow: 'auto' }}>
      <Box sx={{ p: 2, pb: 0 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t('pages.reports.picking.performanceTitle')}</Typography>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('pages.reports.picking.tablePicker')}</TableCell>
            <TableCell align="center">{t('pages.reports.picking.tableWave')}</TableCell>
            <TableCell align="center">{t('pages.reports.picking.tableItem')}</TableCell>
            <TableCell align="center">{t('pages.reports.picking.tableAvgDuration')}</TableCell>
            <TableCell align="center">{t('pages.reports.picking.tableAccuracy')}</TableCell>
            <TableCell align="center">{t('pages.reports.picking.tableEfficiency')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pickers.length === 0 ? (
            <TableRow><TableCell colSpan={6} align="center" sx={{ color: 'text.secondary' }}>{t('pages.reports.noData')}</TableCell></TableRow>
          ) : pickers.map((p, i) => (
            <TableRow key={i} hover>
              <TableCell sx={{ fontWeight: 600 }}>{p.pickerName}</TableCell>
              <TableCell align="center">{p.waveCount}</TableCell>
              <TableCell align="center">{p.itemCount}</TableCell>
              <TableCell align="center">{t('pages.reports.minuteValue', { value: p.avgDuration })}</TableCell>
              <TableCell align="center">
                <Chip label={`%${p.accuracy}`} size="small"
                  color={p.accuracy >= 99 ? 'success' : p.accuracy >= 95 ? 'warning' : 'error'} />
              </TableCell>
              <TableCell align="center">{t('pages.reports.picking.efficiencyValue', { value: p.efficiency })}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>

    {shortPicks.length > 0 && (
      <Paper sx={{ overflow: 'auto' }}>
        <Box sx={{ p: 2, pb: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'error.main' }}>{t('pages.reports.picking.shortPickTitle')}</Typography>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('pages.reports.common.date')}</TableCell>
              <TableCell>{t('pages.reports.common.product')}</TableCell>
              <TableCell>{t('pages.reports.common.location')}</TableCell>
              <TableCell>{t('pages.reports.picking.tablePicker')}</TableCell>
              <TableCell>{t('pages.reports.common.description')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shortPicks.map((sp, i) => (
              <TableRow key={i} hover>
                <TableCell>{sp.date}</TableCell>
                <TableCell>{sp.productName}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{sp.locationCode}</TableCell>
                <TableCell>{sp.pickerName}</TableCell>
                <TableCell>{sp.description || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    )}
    </Box>
  );
};

const ORDER_TYPE_LABEL: Record<string, string> = {
  Single: 'Tekli',
  Multi: 'Çoklu',
  B2B: 'B2B',
};

const STATUS_COLOR: Record<string, 'success' | 'info' | 'default'> = {
  Packed: 'success',
  Shipped: 'info',
};

const PackingTab: React.FC<{ orders: PackingOrderReport[]; isMobile?: boolean }> = ({ orders, isMobile }) => {
  const { t } = useTranslation('warehouse');

  const totalItems = orders.reduce((s, o) => s + o.itemCount, 0);
  const shippedCount = orders.filter(o => o.status === 'Shipped').length;

  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {orders.length > 0 && (
          <Paper sx={{ p: 1.5, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <StatItem label="Toplam Sipariş" value={orders.length} />
            <StatItem label="Toplam Ürün" value={totalItems} />
            <StatItem label="Kargolanan" value={shippedCount} />
          </Paper>
        )}
        {orders.length === 0 ? (
          <Paper sx={{ p: 1.5 }}><Typography color="text.secondary">{t('pages.reports.noData')}</Typography></Paper>
        ) : orders.map((o, i) => (
          <Paper key={i} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{o.orderNumber}</Typography>
              <Chip
                label={o.status === 'Shipped' ? 'Kargolandi' : 'Paketlendi'}
                size="small"
                color={STATUS_COLOR[o.status] ?? 'default'}
              />
            </Box>
            <Typography variant="caption" display="block" color="text.secondary">{o.date}</Typography>
            <Typography variant="body2">{o.itemCount} ürün{o.orderType ? ` • ${ORDER_TYPE_LABEL[o.orderType] ?? o.orderType}` : ''}</Typography>
            {o.shippingMethod && (
              <Typography variant="caption" color="text.secondary">{o.shippingMethod}</Typography>
            )}
          </Paper>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {orders.length > 0 && (
        <Paper sx={{ p: 2, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <StatItem label="Toplam Sipariş" value={orders.length} />
          <StatItem label="Toplam Ürün" value={totalItems} />
          <StatItem label="Kargolanan" value={`${shippedCount} (%${orders.length > 0 ? Math.round(shippedCount / orders.length * 100) : 0})`} />
        </Paper>
      )}
      <Paper sx={{ overflow: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tarih</TableCell>
              <TableCell>Sipariş No</TableCell>
              <TableCell align="center">Tip</TableCell>
              <TableCell align="center">Ürün Adedi</TableCell>
              <TableCell>Kargo Yöntemi</TableCell>
              <TableCell align="center">Durum</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary' }}>
                  {t('pages.reports.noData')}
                </TableCell>
              </TableRow>
            ) : orders.map((o, i) => (
              <TableRow key={i} hover>
                <TableCell>{o.date}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: 13 }}>{o.orderNumber}</TableCell>
                <TableCell align="center">{o.orderType ? (ORDER_TYPE_LABEL[o.orderType] ?? o.orderType) : '-'}</TableCell>
                <TableCell align="center">{o.itemCount}</TableCell>
                <TableCell>{o.shippingMethod ?? '-'}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={o.status === 'Shipped' ? 'Kargolandi' : 'Paketlendi'}
                    size="small"
                    color={STATUS_COLOR[o.status] ?? 'default'}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

const ReceivingTab: React.FC<{ items: ReceivingReportItem[]; isMobile?: boolean }> = ({ items, isMobile }) => {
  const { t } = useTranslation('warehouse');
  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {items.length === 0 ? (
          <Paper sx={{ p: 1.5 }}><Typography color="text.secondary">{t('pages.reports.noData')}</Typography></Paper>
        ) : items.map((r, i) => (
          <Paper key={i} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{r.sessionNumber}</Typography>
            <Typography variant="caption" display="block" color="text.secondary">{r.date}</Typography>
            <Typography variant="body2">{r.productTypes} çeşit • {r.totalItems} ürün</Typography>
            <Chip
              sx={{ mt: 0.75 }}
              label={`${r.placedItems} (%${r.placedPercent})`}
              size="small"
              color={r.placedPercent >= 100 ? 'success' : r.placedPercent >= 80 ? 'warning' : 'error'}
            />
          </Paper>
        ))}
      </Box>
    );
  }
  return (
    <Paper sx={{ overflow: 'auto' }}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{t('pages.reports.receiving.tableDate')}</TableCell>
          <TableCell>{t('pages.reports.receiving.tableDelivery')}</TableCell>
          <TableCell align="center">{t('pages.reports.receiving.tableProductTypes')}</TableCell>
          <TableCell align="center">{t('pages.reports.receiving.tableTotalItems')}</TableCell>
          <TableCell align="center">{t('pages.reports.receiving.tablePlaced')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {items.length === 0 ? (
          <TableRow><TableCell colSpan={5} align="center" sx={{ color: 'text.secondary' }}>{t('pages.reports.noData')}</TableCell></TableRow>
        ) : items.map((r, i) => (
          <TableRow key={i} hover>
            <TableCell>{r.date}</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>{r.sessionNumber}</TableCell>
            <TableCell align="center">{r.productTypes}</TableCell>
            <TableCell align="center">{r.totalItems}</TableCell>
            <TableCell align="center">
              <Chip
                label={`${r.placedItems} (%${r.placedPercent})`}
                size="small"
                color={r.placedPercent >= 100 ? 'success' : r.placedPercent >= 80 ? 'warning' : 'error'}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    </Paper>
  );
};

const CountingTab: React.FC<{ items: CountingReportItem[]; isMobile?: boolean }> = ({ items, isMobile }) => {
  const { t } = useTranslation('warehouse');
  const overallAccuracy = items.length > 0
    ? (items.reduce((s, i) => s + i.accuracyPercent, 0) / items.length).toFixed(1)
    : '100.0';

  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {items.length === 0 ? (
          <Paper sx={{ p: 1.5 }}><Typography color="text.secondary">{t('pages.reports.noData')}</Typography></Paper>
        ) : items.map((c, i) => (
          <Paper key={i} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{c.taskNumber}</Typography>
            <Typography variant="caption" display="block" color="text.secondary">{c.date}</Typography>
            <Typography variant="body2">{c.totalLocations} lokasyon</Typography>
            <Box sx={{ mt: 0.75, display: 'flex', gap: 1 }}>
              <Chip label={`Fark: ${c.discrepancyCount}`} size="small" color={c.discrepancyCount === 0 ? 'success' : 'error'} />
              <Chip label={`%${c.accuracyPercent}`} size="small" color={c.accuracyPercent >= 95 ? 'success' : c.accuracyPercent >= 80 ? 'warning' : 'error'} />
            </Box>
          </Paper>
        ))}
        <Paper sx={{ p: 1.5, textAlign: 'center' }}>
          <Typography variant="body2">
            {t('pages.reports.counting.overallAccuracyLabel')} <strong>%{overallAccuracy}</strong>
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper sx={{ overflow: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('pages.reports.counting.tableTask')}</TableCell>
              <TableCell>{t('pages.reports.counting.tableDate')}</TableCell>
              <TableCell align="center">{t('pages.reports.counting.tableLocation')}</TableCell>
              <TableCell align="center">{t('pages.reports.counting.tableDiscrepancy')}</TableCell>
              <TableCell align="center">{t('pages.reports.counting.tableAccuracy')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow><TableCell colSpan={5} align="center" sx={{ color: 'text.secondary' }}>{t('pages.reports.noData')}</TableCell></TableRow>
            ) : items.map((c, i) => (
              <TableRow key={i} hover>
                <TableCell sx={{ fontWeight: 600 }}>{c.taskNumber}</TableCell>
                <TableCell>{c.date}</TableCell>
                <TableCell align="center">{c.totalLocations}</TableCell>
                <TableCell align="center">
                  <Chip label={c.discrepancyCount} size="small"
                    color={c.discrepancyCount === 0 ? 'success' : 'error'} />
                </TableCell>
                <TableCell align="center">
                  <Chip label={`%${c.accuracyPercent}`} size="small"
                    color={c.accuracyPercent >= 95 ? 'success' : c.accuracyPercent >= 80 ? 'warning' : 'error'} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Paper sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body1">
          {t('pages.reports.counting.overallAccuracyLabel')} <strong>%{overallAccuracy}</strong>
        </Typography>
      </Paper>
    </Box>
  );
};

const StatItem: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}</Typography>
    <Typography variant="body1" sx={{ fontWeight: 600 }}>{value}</Typography>
  </Box>
);

const LegendDot: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    <Box sx={{ width: 8, height: 8, bgcolor: color, borderRadius: '50%' }} />
    <Typography variant="caption">{label}</Typography>
  </Box>
);

export default WmsReports;
